import { FC, PropsWithChildren, useEffect, useState } from 'react'
import {
  motion,
  MotionValue,
  useMotionValue,
  useScroll,
  useSpring,
  useTransform,
  useVelocity,
} from 'framer-motion'

import { config } from 'config'
import { isCurrentPage } from 'helpers'
import { Container } from 'components/container'
import { IconHomeOutline } from 'components/icons/home-outline'
import { IconHomeFill } from 'components/icons/home-fill'
import { IconShare } from 'components/icons/share'
import { AnimatePresence } from 'framer-motion'
import { theme } from 'styles/theme'
import { IconClose } from 'components/icons/close'
import { useMedia } from 'hooks/useMedia'
import { breakpoints } from 'styles/mq'
import { IconMenu } from 'components/icons/menu'
import { Facebook } from './icons/facebook'
import { Telegram } from './icons/telegram'
import { Twitter } from './icons/twitter'
import { buildShareUrl } from './helpers'
import { Submenu } from './submenu'
import { variants } from './variants'
import {
  StyledHeader,
  Logo,
  LogoContainer,
  Menu,
  IconDefault,
  IconActive,
  ShareButton,
  ShareItem,
  MenuToggle,
  MobileMenuContainer,
  ShareMenu,
  MenuItem,
  Backdrop,
} from './styled'

const HomeWrapper: FC<PropsWithChildren<{ onClick: () => void }>> = ({
  onClick,
  children,
}) => {
  const home = config.pages.find((p) => !p.label)!
  return (
    <MenuItem>
      {isCurrentPage(home) ? (
        <>
          <button disabled>{children}</button>

          <Submenu onClick={onClick} />
        </>
      ) : (
        <a href={`${config.urlBefore}${home?.id}${config.urlAfter}`}>
          {children}
        </a>
      )}
    </MenuItem>
  )
}

export const Header: FC<{
  isFluid?: boolean
  isSticky?: boolean
}> = ({ isFluid, isSticky = false }) => {
  const isMenuVisibleByDefault = useMedia([breakpoints.large], [true], false)
  const [isMenuVisible, setMenuVisible] = useState(isMenuVisibleByDefault)
  const [isShareExpanded, setShareExpanded] = useState(false)

  const { scrollY } = useScroll()
  const scrollVelocity = useVelocity(scrollY)
  const scrollTrend = useMotionValue(0)
  const transform = useTransform(() =>
    scrollY.get() < 100 || scrollTrend.get() < 0 || !isSticky ? '0%' : '-100%'
  )
  const translateY = useSpring(transform as MotionValue<string>, {
    stiffness: 1000,
    damping: 67,
    mass: 1,
  })

  useEffect(() => {
    setMenuVisible(isMenuVisibleByDefault)
  }, [isMenuVisibleByDefault])

  useEffect(() => {
    scrollVelocity.on('change', (v) => v !== 0 && scrollTrend.set(v))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <StyledHeader $isSticky={isSticky} style={{ translateY }}>
        <Container $mode={isFluid ? 'fluid' : 'default'}>
          <LogoContainer href="/">
            <Logo />
          </LogoContainer>

          <MenuToggle
            onClick={() => setMenuVisible((v) => !v)}
            $isVisible={!isMenuVisibleByDefault}
          >
            <IconMenu />
          </MenuToggle>

          <AnimatePresence>
            {isMenuVisible && (
              <>
                {!isMenuVisibleByDefault && (
                  <Backdrop
                    key="backdrop"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={() => setMenuVisible(false)}
                  />
                )}
                <MobileMenuContainer
                  key="container"
                  initial={{ height: 0 }}
                  animate={{
                    height: 'auto',
                    transition: {
                      type: 'tween',
                      duration: 0.4,
                      ease: theme.easeValues.out,
                    },
                  }}
                  exit={{
                    height: 0,
                    transition: {
                      type: 'tween',
                      duration: 0.4,
                      ease: theme.easeValues.inOut,
                    },
                  }}
                >
                  <AnimatePresence initial={false} mode="popLayout">
                    {!isShareExpanded && (
                      <Menu
                        key="menu"
                        variants={variants}
                        initial="down"
                        animate="animate"
                        exit="down"
                      >
                        <HomeWrapper
                          onClick={() => setMenuVisible(isMenuVisibleByDefault)}
                        >
                          <IconDefault as={IconHomeOutline} />
                          <IconActive as={IconHomeFill} />
                        </HomeWrapper>

                        {config.pages
                          .filter((p) => !!p.label)
                          .map((page) => (
                            <MenuItem key={page.id}>
                              {isCurrentPage(page) ? (
                                <button disabled>{page.label}</button>
                              ) : (
                                <a
                                  href={`${config.urlBefore}${page.id}${config.urlAfter}`}
                                >
                                  {page.label}
                                </a>
                              )}

                              {isCurrentPage(page) && (
                                <Submenu
                                  onClick={() =>
                                    setMenuVisible(isMenuVisibleByDefault)
                                  }
                                />
                              )}
                            </MenuItem>
                          ))}
                      </Menu>
                    )}

                    {(isShareExpanded || !isMenuVisibleByDefault) && (
                      <ShareMenu
                        key="share"
                        variants={variants}
                        initial="up"
                        animate="animate"
                        exit="up"
                      >
                        <li>
                          <ShareItem href={buildShareUrl('facebook')}>
                            <Facebook />
                          </ShareItem>
                        </li>
                        <li>
                          <ShareItem href={buildShareUrl('telegram')}>
                            <Telegram />
                          </ShareItem>
                        </li>
                        <li>
                          <ShareItem href={buildShareUrl('twitter')}>
                            <Twitter />
                          </ShareItem>
                        </li>
                      </ShareMenu>
                    )}

                    {isMenuVisibleByDefault && (
                      <ShareButton
                        key="share-toggle"
                        onClick={() => setShareExpanded((v) => !v)}
                      >
                        <AnimatePresence initial={false} mode="popLayout">
                          {isShareExpanded ? (
                            <motion.div
                              key="close"
                              variants={variants}
                              initial="up"
                              animate="animate"
                              exit="up"
                            >
                              <IconClose />
                            </motion.div>
                          ) : (
                            <motion.div
                              key="open"
                              variants={variants}
                              initial="down"
                              animate="animate"
                              exit="down"
                            >
                              <IconShare />
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </ShareButton>
                    )}
                  </AnimatePresence>
                </MobileMenuContainer>
              </>
            )}
          </AnimatePresence>
        </Container>
      </StyledHeader>
    </>
  )
}
